import React, { useEffect, useMemo, useRef } from "react";
import { observer } from "mobx-react";
import { MusicalGenreViewStore } from "../../store";
import { MusicalGenreView } from "./MusicalGenreView";
import { MusicalGenreUpdateStore } from "../../store";
import { MusicalGenreUpdate } from "../update/MusicalGenreUpdate";
import { useRootStore } from "../../../../store/contexts/RootStoreContext";
import { Box, CircularProgress } from "@mui/material";

export type MusicalGenreDrawerContentProps = {
  entityId: string;
  mode?: "view" | "update";
};

export const MusicalGenreDrawerContent: React.FC<MusicalGenreDrawerContentProps> =
  observer(({ entityId, mode = "view" }) => {
    const rootStore = useRootStore();
    const viewInitializedRef = useRef<string | null>(null);
    const updateInitializedRef = useRef<string | null>(null);

    const viewStore = useMemo(() => {
      const s = new MusicalGenreViewStore(rootStore);
      s.isDrawerMode = true;
      return s;
    }, [rootStore]);

    useEffect(() => {
      if (
        mode === "view" &&
        entityId &&
        viewInitializedRef.current !== entityId
      ) {
        viewInitializedRef.current = entityId;
        viewStore.initialize({ id: entityId });
      }
    }, [entityId, mode, viewStore]);

    const updateStore = useMemo(() => {
      const s = new MusicalGenreUpdateStore(rootStore);
      s.isDrawerMode = true;
      return s;
    }, [rootStore]);

    useEffect(() => {
      if (
        mode === "update" &&
        entityId &&
        updateInitializedRef.current !== entityId
      ) {
        updateInitializedRef.current = entityId;
        updateStore.initialize({ id: entityId });
      }
    }, [entityId, mode, updateStore]);

    if (mode === "update") {
      if (
        updateStore.status === "processing" ||
        updateStore.status === "uninitialized"
      ) {
        return (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: 200,
            }}
          >
            <CircularProgress />
          </Box>
        );
      }
      return <MusicalGenreUpdate musicalGenreUpdateStore={updateStore} />;
    }

    if (
      viewStore.status === "processing" ||
      viewStore.status === "uninitialized"
    ) {
      return (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: 200,
          }}
        >
          <CircularProgress />
        </Box>
      );
    }

    return <MusicalGenreView musicalGenreViewStore={viewStore} />;
  });
